import { type ReactNode, type RefObject } from "react";
import type { DirectiveChoiceSlot } from "../../core/config";
import { shouldSubmitEmptyOnEnterForNextChoice } from "../interaction/enterOwnership";
import {
  CHIP_NEXT_CHOICE_SURFACE,
  DIRECTIVE_BUILDER_CHOICE_FIELD_ID,
  DIRECTIVE_CHIP_CHOICE_FIELD_ATTR,
  DIRECTIVE_CHIP_NEXT_CHOICE_TARGET_ATTR,
  DIRECTIVE_FLOATING_CHOICE_FIELD_ATTR,
} from "../../suggestion/storage/session";
import type { DirectiveChipStrategy } from "../../core/chipStrategy";
import { DirectiveChoiceSlotControl } from "../interaction/choiceSlotControl";
import { CONSOLE_OVERLAY_PANEL_CLASS } from "@/lib/ui/consoleOverlayPanel";
import { cn } from "@/lib/utils";

export type DirectiveBuilderChoicePanelProps = {
  variant: "floating" | "chip-edit" | "chip-next";
  choiceStepKey: number;
  choiceFieldId?: string;
  choiceSlot: DirectiveChoiceSlot;
  choiceInitialValue: string;
  onChoiceSubmit: (value: string) => void;
  stepTitle?: string | undefined;
  chipNextChoiceContext?: {
    strategy: DirectiveChipStrategy | null;
  };
  choiceInputRef?: RefObject<HTMLInputElement | null>;
  choiceSelectTriggerRef?: RefObject<HTMLButtonElement | null>;

  floatingAllowedTriggerRef?: RefObject<HTMLButtonElement | null>;
};

export const DirectiveBuilderChoicePanel = ({
  variant,
  choiceStepKey,
  choiceFieldId,
  choiceSlot,
  choiceInitialValue,
  onChoiceSubmit,
  stepTitle,
  chipNextChoiceContext,
  choiceInputRef,
  choiceSelectTriggerRef,
  floatingAllowedTriggerRef,
}: DirectiveBuilderChoicePanelProps): ReactNode => {
  const fieldId = choiceFieldId ?? DIRECTIVE_BUILDER_CHOICE_FIELD_ID;
  const label = (
    <>
      {choiceSlot.label}
      {choiceSlot.placeholder != null ? ` (${choiceSlot.placeholder})` : ""}
    </>
  );

  if (variant === "chip-edit") {
    return (
      <div
        className="space-y-2"
        data-directive-chip-choice-slot="edit"
        data-directive-chip-choice-index={choiceStepKey}
        {...{ [DIRECTIVE_CHIP_CHOICE_FIELD_ATTR]: "" }}
      >
        <DirectiveChoiceSlotControl
          slot={choiceSlot}
          value={choiceInitialValue ?? ""}
          onCommit={onChoiceSubmit}
          fieldId={fieldId}
          label={label}
          remountKey={choiceStepKey}
          variant="chip-edit"
        />
      </div>
    );
  }

  if (variant === "chip-next") {
    return (
      <div
        className="space-y-2"
        data-directive-session-surface={CHIP_NEXT_CHOICE_SURFACE}
        {...{
          [DIRECTIVE_CHIP_NEXT_CHOICE_TARGET_ATTR]: "",
          [DIRECTIVE_CHIP_CHOICE_FIELD_ATTR]: "",
        }}
        onKeyDown={(e) => {
          const strat = chipNextChoiceContext?.strategy;
          if (strat != null && e.key === "Enter") {
            if (!shouldSubmitEmptyOnEnterForNextChoice(e.target)) {
              return;
            }
            e.preventDefault();
            onChoiceSubmit("");
          }
        }}
      >
        <DirectiveChoiceSlotControl
          slot={choiceSlot}
          value={choiceInitialValue ?? ""}
          onCommit={onChoiceSubmit}
          fieldId={fieldId}
          label={label}
          remountKey={choiceStepKey}
          variant="chip-next"
          suggestedInputRef={choiceInputRef}
          allowedTriggerRef={choiceSelectTriggerRef}
          freeformInputRef={choiceInputRef}
        />
      </div>
    );
  }

  return (
    <div
      className={cn("space-y-2 p-2", CONSOLE_OVERLAY_PANEL_CLASS)}
      data-console-overlay-panel=""
      data-directive-session-surface="floating-choice"
      {...{ [DIRECTIVE_FLOATING_CHOICE_FIELD_ATTR]: "" }}
    >
      {stepTitle != null && (
        <p className="text-sm text-foreground/45">{stepTitle}</p>
      )}
      <DirectiveChoiceSlotControl
        slot={choiceSlot}
        value={choiceInitialValue ?? ""}
        onCommit={onChoiceSubmit}
        fieldId={fieldId}
        label={label}
        remountKey={choiceStepKey}
        variant="floating"
        freeformAutoFocus={false}
        floatingAllowedTriggerRef={floatingAllowedTriggerRef}
      />
    </div>
  );
};
